import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Table, Card, FilterValues, Filter, PageHeader, Button, FilterTypes, QuickFilterTypes } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";

import { Language, usePermission } from "imports/Utils";

import { DossierLink, LocationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import InactivityTypeSelect from "./components/selects/InactivityTypeSelect";

import { LocationType, SortDirection } from "__generated__/enums";
import { GetInactivitiesDocument, GetInactivitiesQuery } from "__generated__/view/inactivities/graphql/inactivities.queries.generated";
import { DeleteInactivityDocument } from "__generated__/view/inactivities/graphql/inactivities.mutations.generated";
import { Box } from "@mui/material";
import { ChildPropsFromPagination } from "types/commons";

type InactivityFromQuery = ChildPropsFromPagination<GetInactivitiesQuery, "inactivities">;

const Inactivities = () => {

	const confirm = useConfirm();
	const client = useApolloClient();
	const navigate = useNavigate();

	const { isAdmin, isCompanyUser, isMasterdata, isReadOnly } = usePermission();

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.Inactivities,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetInactivitiesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "start",
								direction: SortDirection.Desc,
							} ],
						},
						inactivitySearch: {
							...filter,
							ships: filter.ships?.map((ship: ShipSelectOption) => ship.id),
							locations: filter.locations?.map((location: LocationSelectOption) => location.id),
							fromLocations: filter.fromLocations?.map((location: LocationSelectOption) => location.id),
							toLocations: filter.toLocations?.map((location: LocationSelectOption) => location.id),
						},
					},
				});

				callback({
					content: result.data.inactivities?.content ?? [],
					totalResults: result.data.inactivities?.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deleteInactivity ] = useMutation(DeleteInactivityDocument, {
		onCompleted: () => refetch(),
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("INACTIVITIES")}
					<Helmet title={intl.formatMessage({ id: "INACTIVITIES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/inactivities/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "inactivityNumber",
								title: intl.formatMessage({ id: "INACTIVITY_NUMBER" }),
							}, {
								id: "type",
								title: intl.formatMessage({ id: "TYPE" }),
								renderFilterValue: (value) => Language(`${value}_TIME`),
							}, {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIPS" }),
								renderFilterValue: (value) => value.map((option: ShipSelectOption) => option.name).join(", "),
							}, {
								id: "locations",
								title: intl.formatMessage({ id: "LOCATIONS" }),
								renderFilterValue: (value) => value.map((option: LocationSelectOption) => option.description).join(", "),
							}, {
								id: "fromLocations",
								title: intl.formatMessage({ id: "VOYAGE_STARTLOCATIONS" }),
								renderFilterValue: (value) => value.map((option: LocationSelectOption) => option.description).join(", "),
							}, {
								id: "toLocations",
								title: intl.formatMessage({ id: "VOYAGE_ENDLOCATIONS" }),
								renderFilterValue: (value) => value.map((option: LocationSelectOption) => option.description).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "inactivityNumber",
								title: intl.formatMessage({ id: "INACTIVITY_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "type",
								title: intl.formatMessage({ id: "TYPE" }),
								render: (value, onChange)  => (
									<InactivityTypeSelect
										value={value}
										onChange={onChange} />
								),
							}, {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIPS" }),
								render: (value, onChange) => (
									<ShipSelect
										value={value}
										onChange={onChange}
										multiple />
								),
							}, {
								id: "locations",
								title: intl.formatMessage({ id: "LOCATIONS" }),
								render: (value, onChange) => (
									<LocationSelect
										value={value}
										types={[ LocationType.WaitingArea, LocationType.Wharf ]}
										onChange={onChange}
										multiple />
								),
							}, {
								id: "fromLocations",
								title: intl.formatMessage({ id: "VOYAGE_STARTLOCATIONS" }),
								defaultVisible: false,
								render: (value, onChange) => (
									<LocationSelect
										value={value}
										onChange={onChange}
										multiple />
								),
							}, {
								id: "toLocations",
								title: intl.formatMessage({ id: "VOYAGE_ENDLOCATIONS" }),
								defaultVisible: false,
								render: (value, onChange) => (
									<LocationSelect
										value={value}
										onChange={onChange}
										multiple />
								),
							} ]} />
					)}
					columns={[ {
						id: "inactivityNumber",
						sortKey: "voyageNumber",
						title: intl.formatMessage({ id: "INACTIVITY_NUMBER" }),
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
						render: (row: InactivityFromQuery) => row.voyageNumber,
					}, {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						sortable: true,
						render: (row: InactivityFromQuery) => Language(`${row.type}_TIME`),
						filterComponent: (value, onChange) => (
							<InactivityTypeSelect
								value={value}
								label={intl.formatMessage({ id: "TYPE" })}
								onChange={onChange} />
						),
					}, {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						render: (row: InactivityFromQuery) => <DossierLink charter={row.purchaseCharters?.[0]} />,
						filterComponent: "TEXT",
					}, {
						id: "ships",
						sortKey: "ship.name",
						title: intl.formatMessage({ id: "VOYAGES_SHIP" }),
						sortable: true,
						render: (row: InactivityFromQuery) => <ShipLink ship={row.ship} />,
						filterComponent: (value, onChange) => (
							<ShipSelect
								value={value}
								onChange={onChange}
								label={intl.formatMessage({ id: "VOYAGES_SHIP" })}
								multiple />
						),
					}, {
						id: "start",
						title: intl.formatMessage({ id: "START_END_WINDOW" }),
						sortable: true,
						render: (row: InactivityFromQuery) => <FormattedDateWindow from={row.start} to={row.end} withoutYear />,
						width: 225,
					}, {
						id: "locations",
						title: intl.formatMessage({ id: "INACTIVITY_LOCATION" }),
						render: (row: InactivityFromQuery) => <LocationLink location={row.calls?.[0]?.location} />,
						filterComponent: (value, onChange) => (
							<LocationSelect
								value={value}
								types={[ LocationType.WaitingArea, LocationType.Wharf ]}
								onChange={onChange}
								label={intl.formatMessage({ id: "INACTIVITY_LOCATION" })}
								multiple />
						),
					}, {
						id: "fromLocations",
						sortKey: "from.description",
						title: intl.formatMessage({ id: "VOYAGE_STARTLOCATION" }),
						defaultVisible: false,
						sortable: true,
						render: (row: InactivityFromQuery) => <LocationLink location={row.from} />,
						filterComponent: (value, onChange) => (
							<LocationSelect
								value={value}
								onChange={onChange}
								label={intl.formatMessage({ id: "VOYAGE_STARTLOCATION" })}
								multiple />
						),
					}, {
						id: "toLocations",
						sortKey: "to.description",
						title: intl.formatMessage({ id: "VOYAGE_ENDLOCATION" }),
						defaultVisible: false,
						sortable: true,
						render: (row: InactivityFromQuery) => <LocationLink location={row.to} />,
						filterComponent: (value, onChange) => (
							<LocationSelect
								value={value}
								onChange={onChange}
								label={intl.formatMessage({ id: "VOYAGE_ENDLOCATION" })}
								multiple />
						),
					}, {
						id: "created",
						title: intl.formatMessage({ id: "CREATED" }),
						sortable: true,
						render: (row: InactivityFromQuery) => <FormattedDate date={row.created} withoutYear />,
						width: 170,
					} ]}
					rowActions={(isAdmin || isCompanyUser || isMasterdata) && !isReadOnly ? [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/inactivities/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteInactivity({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ] : undefined} />
			</Card>
		</div>
	);
};

export default Inactivities;
